import * as React from 'react';
import {classNames} from '@shopify/react-utilities/styles';
import {withI18n, WithI18nProps} from 'packages/@shopify/react-i18n';
import {Icon} from 'components/UIKit';

import {Logo, NavSearch, NavDrawer, NavItem, MegaNavPopover} from './components';
import {NavItemData, SocialLinks} from './interfaces';
import * as styles from './Nav.scss';
import * as locales from './locales';

// @TODO: When we upgrade to typescript 3.0 we can use `static defaultProps`
// (https://blogs.msdn.microsoft.com/typescript/2018/07/30/announcing-typescript-3-0/#default-props-support)
const DEFAULT_NAV_ATTRIBUTES = {
  ariaLabel: 'Main Navigation',
  itemScope: true,
  itemType: 'https://schema.org/SiteNavigationElement',
  role: 'navigation',
};

interface NavProps {
  ariaLabel?: string;
  itemScope?: boolean;
  itemType?: string;
  role?: string;
  searchAction: string;
  shopNavItems: NavItemData[];
  infoNavItems: NavItemData[];
  phoneContact: NavItemData;
  callsToAction: NavItemData[];
  socialLinks: SocialLinks[];
}

interface NavState {
  drawerOpen: boolean;
  megaNavOpen: boolean;
  openNavs: string[];
  searchOpen: boolean;
  searchTerm: string;
}

function setBodyOverflow(visible: boolean) {
  document.body.style.overflow = visible ? 'visible' : 'hidden';
}

export class NavComponent extends React.Component<NavProps & WithI18nProps, NavState> {
  constructor(props) {
    super(props);
    this.state = {
      drawerOpen: false,
      megaNavOpen: false,
      openNavs: [],
      searchOpen: false,
      searchTerm: '',
    };
  }

  handleSearchOpen = () => {
    this.setState({searchOpen: true});
    setBodyOverflow(false);
  };

  handleSearchCancel = () => {
    this.setState({searchOpen: false}, () => {
      setBodyOverflow(true);
    });
  };

  handleSearchInputChange = (newValue: string) => {
    this.setState({searchTerm: newValue});
  };

  handleDrawerOpen = () => {
    this.setState({drawerOpen: true});
    setBodyOverflow(false);
  };

  handleDrawerClose = () => {
    this.setState({drawerOpen: false, openNavs: []}, () => {
      setBodyOverflow(true);
    });
  };

  handleMegaNavOpen = () => {
    this.setState({megaNavOpen: true});
  };

  handleMegaNavClose = () => {
    this.setState({megaNavOpen: false, openNavs: []});
  };

  handleActiveNestedNavs = (itemPath) => {
    this.setState({openNavs: itemPath});
  };

  handleBackButton = () => {
    this.setState((prevState) => {
      prevState.openNavs.splice(-1);
      const previousNavView = prevState.openNavs;
      return {openNavs: previousNavView};
    });
  };

  render() {
    const {
      megaNavOpen,
      openNavs,
      searchOpen,
      searchTerm,
      drawerOpen,
    } = this.state;

    const {
      ariaLabel = DEFAULT_NAV_ATTRIBUTES.ariaLabel,
      itemScope = DEFAULT_NAV_ATTRIBUTES.itemScope,
      itemType = DEFAULT_NAV_ATTRIBUTES.itemType,
      role = DEFAULT_NAV_ATTRIBUTES.role,
      shopNavItems,
      infoNavItems,
      phoneContact,
      callsToAction,
      socialLinks,
      searchAction,
      i18n,
    } = this.props;

    return (
      <nav
        id="ExchangeMainNav"
        aria-label={ariaLabel}
        className={styles.nav}
        itemScope={itemScope}
        itemType={itemType}
        role={role}
      >
        <a
          data-ga-event="Main Nav"
          data-ga-action="Skip to content"
          href="#Main"
          className={styles.skip}
        >
          {i18n.translate('skip')}
        </a>
        <Logo />

        <div className={classNames(styles.navGroup, styles.navGroupLeft)}>
          <ul className={classNames(styles.navItems, styles.desktopNav)}>
            <MegaNavPopover
              label={i18n.translate('browse')}
              open={megaNavOpen}
              openNavs={openNavs}
              onOpen={this.handleMegaNavOpen}
              onBack={this.handleBackButton}
              onClose={this.handleMegaNavClose}
              onActiveNavs={this.handleActiveNestedNavs}
              shopNavItems={shopNavItems}
              infoNavItems={infoNavItems}
            />
          </ul>

          <button className={styles.openSearch} onClick={this.handleSearchOpen} type="button">
            <Icon fill="white" icon="search" size={20} aria-label={i18n.translate('search.open')} />
          </button>
        </div>

        <div className={classNames(styles.navGroup, styles.navGroupRight)}>
          <ul className={classNames(styles.navItems, styles.desktopNav)}>
            <NavItem
              key={phoneContact.internalName}
              url={phoneContact.url}
              label={phoneContact.title}
            />

            {callsToAction.map((cta) =>
              <NavItem
                key={cta.internalName}
                url={cta.url}
                label={cta.title}
                display={cta.button ? 'button' : undefined}
                tracking={cta.tracking}
              />)}
          </ul>

          <button className={classNames(styles.deviceNav, styles.menu)} onClick={this.handleDrawerOpen} type="button">
            <Icon fill="white" icon="menu" size={20} aria-label={i18n.translate('drawer.menu')} />
          </button>
        </div>

        <NavSearch
          onCancel={this.handleSearchCancel}
          onChange={this.handleSearchInputChange}
          open={searchOpen}
          searchAction={searchAction}
          searchTerm={searchTerm}
        />

        <NavDrawer
          open={drawerOpen}
          openNavs={openNavs}
          wrapperClass={styles.deviceNav}
          onBack={this.handleBackButton}
          onCancel={this.handleDrawerClose}
          onActiveNavs={this.handleActiveNestedNavs}
          shopNavItems={shopNavItems}
          infoNavItems={infoNavItems}
          phoneContact={phoneContact}
          callsToAction={callsToAction}
          socialLinks={socialLinks}
        />
      </nav>
    );
  }
}

export const Nav = withI18n({
  id: 'Nav',
  fallback: locales.en,
})(NavComponent);

export default Nav;
